<template>
  <div class="card">

    <h4 class="mt-2 mb-4">{{$store.getters.t('pet_info_socialization')}}</h4>

    <div class="info d-flex">
        <div class="info__item" :style="{maxWidth: '100%'}">
            <div class="info__passport">
                <div class="info__header d-flex justify-content-between align-items-start">
                    <div class="info__avatar rounded overflow-hidden">
                        <img :src="avatar" alt="">
                    </div>
                    <div class="info__meta">
                        <span v-text="createdAt"></span>
                        <span v-text="id"></span>
                    </div>
                </div>
                <div class="info__title">
                    {{ name }},
                    <span v-text="city"></span>
                </div>
                <div class="info__labels mb-2 d-flex flex-wrap">
                    <span v-text="gender"></span>
                    <span v-text="age"></span>
                    <span v-text="weight"></span>
                </div>
                <div class="info__labels d-flex">
                    {{$store.getters.t('pet_info_size_label')}} {{ size }}
                </div>
            </div>
        </div>
        <div class="info__item" v-if="showSociality">
            <div class="info-rating d-flex justify-content-between flex-column">
                <div class="info-rating__line justify-content-between d-flex p-3" v-if="enrichment || toChildren">
                    <div class="info-rating__item d-flex flex-column" v-if="enrichment">
                        <div class="info-rating__icon">
                            <img src="/assets/images/icons/pet-home.svg" alt="">
                        </div>
                        <div class="info-rating__title">{{$store.getters.t('pet_info_home_behavior')}}</div>
                        <div class="info-rating__info"><span v-text="enrichment"></span>/5</div>
                    </div>
                    <div class="info-rating__item d-flex flex-column" v-if="toChildren">
                        <div class="info-rating__icon">
                            <img src="/assets/images/icons/child.svg" alt="">
                        </div>
                        <div class="info-rating__title">{{$store.getters.t('pet_info_to_children')}}</div>
                        <div class="info-rating__info"><span v-text="toChildren"></span>/5</div>
                    </div>
                </div>
                <div class="info-rating__line justify-content-between d-flex p-3" v-if="toDogs || toCats">
                    <div class="info-rating__item d-flex flex-column" v-if="toDogs">
                        <div class="info-rating__icon">
                            <img src="/assets/images/icons/dog.svg" alt="">
                        </div>
                        <div class="info-rating__title">{{$store.getters.t('pet_info_to_dogs')}}</div>
                        <div class="info-rating__info"><span v-text="toDogs"></span>/5</div>
                    </div>
                    <div class="info-rating__item d-flex flex-column" v-if="toCats">
                        <div class="info-rating__icon">
                            <img src="/assets/images/icons/cat.svg" alt="">
                        </div>
                        <div class="info-rating__title">{{$store.getters.t('pet_info_to_cats')}}</div>
                        <div class="info-rating__info"><span v-text="toCats"></span>/5</div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <template v-if="showHealthInfo">

        <h4 class="my-4">{{$store.getters.t('pet_info_health')}}</h4>

        <div class="health d-flex">
            <div 
                v-if="isParasiteTreatment"
                class="health__item d-flex align-items-center justify-content-center py-3 px-2"
            >
                <div class="health__icon">
                    <img src="/assets/images/icons/parasite.svg" alt="">
                </div>
                <span>{{$store.getters.t('pet_info_parasite_treatment')}}</span>
            </div>
            <div 
                v-if="isVaccinated"
                class="health__item d-flex align-items-center justify-content-center py-3 px-2"
            >
                <div class="health__icon">
                    <img src="/assets/images/icons/vaccine.svg" alt="">
                </div>
                <span>{{$store.getters.t('pet_info_vaccinated')}}</span>
            </div>
            <div 
                v-if="isSterilized"
                class="health__item d-flex align-items-center justify-content-center py-3 px-2"
            >
                <div class="health__icon">
                    <img src="/assets/images/icons/health.svg" alt="">
                </div>
                <span>{{$store.getters.t('pet_info_sterilized')}}</span>
            </div>
        </div>

    </template>

  </div>
</template>

<script>
import { sizes, weight } from '../../constants/pet-meta.js'

export default {
    name: 'PetInfo',

    props: {
        petInfo: {
            required: true,
            type: Object,
            default: () => {}
        }
    },

    computed: {

        avatar(){
            return `/storage/${(this.petInfo?.avatar?.path).replace('original', 'avatar')}`
        },

        name(){
            return this.petInfo.name
        },

        city(){
            return this.petInfo?.city?.name ?? ''
        },

        gender(){
            return this.petInfo.gender === 'male' ? this.$store.getters.t('pet_info_gender_male') : this.$store.getters.t('pet_info_gender_female')
        },

        age(){
            return this.petInfo?.petAgeCategory?.name ?? this.$store.getters.t('common_unknown')
        },

        weight(){
            return weight[this.petInfo.weight]  ?? this.$store.getters.t('pet_info_zero_kg')
        },

        size(){
            return sizes[this.petInfo.size] ?? this.$store.getters.t('common_unknown')
        },

        id(){
            return `id: ${this.petInfo.id}`
        },

        createdAt(){
            return ((new Date(this.petInfo.created_at))?.toLocaleDateString("en-GB") ?? '').replaceAll('/', '.')
        },

        enrichment(){
            return this.petInfo.enrichment ?? 0
        },

        toChildren(){
            return this.petInfo.to_children ?? 0
        },

        toDogs(){
            return this.petInfo.to_dogs ?? 0
        },
        
        toCats(){
            return this.petInfo.to_cats ?? 0
        },

        isParasiteTreatment(){
            return this.petInfo.is_parasite_treatment
        },

        isSterilized(){
            return this.petInfo.is_sterilized
        },

        isVaccinated(){
            return this.petInfo.is_vaccinated
        },

        showHealthInfo(){
            let { isParasiteTreatment, isSterilized, isVaccinated } = this
            return isParasiteTreatment || isSterilized || isVaccinated
        },
        
        showSociality(){
            let { toChildren, toCats, toDogs, enrichment } = this
            return toChildren || toCats || toDogs || enrichment
        }

    }

}
</script>

<style lang="scss" scoped>
    @mixin pseudo {
        position: absolute;
        content: '';
        display: block;
    }
    .card{
        padding: 24px;
        border-radius: 4px;
        border: none;
        box-shadow: 0 2px 4px rgb(108 111 115 / 10%);
        margin-bottom: 30px;

        @media screen and(max-width: 480px) {
            padding: 24px 12px;

        }

    }

    .info{
        column-gap: 8px;
        &__item{
            flex-grow: 1;
            max-width: 50%;
        }

        &__passport{
            color: #fff;
            border-radius: 12px;
            background: url('/assets/images/info-passport.jpg') no-repeat center/cover;
            font-size: 12px; 
            padding: 24px;
        }

        &__avatar{
            width: 72px;
            height: 72px;
            border: 2px solid #699FF2;

            img{
                width: 100%;
                height: 100%;
                background: transparent;
            }

        }

        &__meta{
            span{
                padding: 8px 13px;
                border-radius: 8px;

                & + span{
                    background: #2E76E4;
                }

            }
        }

        &__title{
            font-size: 25px;
            font-weight: 700;
            margin: 16px 0 28px;

            span{
                font-weight: normal;
                font-size: 16px;
            }

        }

        &__labels{
            span{
                display: inline-block;
                position: relative;

                & + span {
                    padding-left: 8px;
                    &::before{
                        @include pseudo();
                        background: #fff;
                        left: 4px;
                        top: 50%;
                        transform: translateY(-50%);
                        width: 1px;
                        height: 8px;
                        opacity: .5;
                    }
                }

            }

        }

        &-rating{
            font-size: 12px;
            row-gap: 8px;
            height: 100%;
            &__line{
                background: #F5F9FF;
                border-radius: 12px;
                column-gap: 16px;
                flex-grow: 1;
                max-height: 50%;
            }

            &__item{
                position: relative;
                flex-grow: 1;
                flex-shrink: 0;
                width: 50%;
                max-width: 133px;
                & + & {
                    &::before{
                        @include pseudo();
                        left: -16px;
                        width: 1px;
                        top: 0;
                        bottom: 0;
                        background: #fff;
                    }
                }

            }

            &__icon{
                width: 16px;
                height: 16px;
                margin-bottom: 8px;
                img{
                    width: 100%;
                    height: 100%;
                }
            }

            &__title{
                flex-grow: 1;
            }

            &__info{
                color: #2E76E4;

                span{
                    font-size: 18px;
                    color: #333;
                    font-weight: 700;
                }

            }   
        }

        @media screen and (max-width: 768px){
            flex-wrap: wrap;

            &__item{
                max-width: 100%;
            }

            &__passport{
                margin-bottom: 20px;
            }

            &-rating{

                &__line{
                    max-height: unset;
                }
            
                &__item{
                    max-width: 50%;
                }
            
            }

        }

        @media screen and (max-width: 480px){

            &__passport{
                padding: 24px 10px;
            }

            &__meta{
                display: flex;
                flex-direction: column-reverse;
            }

            &-rating{

                &__line{
                    flex-direction: column;
                    align-items: center;
                    row-gap: 32px;
                }

                &__item{
                    text-align: center;

                    & + & {
                        &::before{
                            left: 0;
                            right: 0;
                            top: -16px;
                            transform: unset;
                            bottom: unset;
                            height: 1px;
                            width: auto;
                        }
                    }

                }

                &__icon{
                    margin: 0 auto 15px;
                }

            }

        }

    }

    .health{
        font-size: 12px;
        line-height: 16px;
        column-gap: 9px;

        &__item{
            background: #F5F9FF;
            border-radius: 12px;
            flex-grow: 1;
            max-width: 33.33%;

            span{
                width: 100%;
                max-width: 120px;
            }
        }

        &__icon{
            width: 16px;
            height: 16px;
            margin-right: 15px;
            flex-shrink: 0;
            img{
                width: 100%;
                height: 100%;
            }
        }

        @media screen and( max-width: 768px ){
            flex-wrap: wrap;
            row-gap: 9px;
            &__item{
                max-width: 50%;
            }
        }

        @media screen and( max-width: 480px ){
            flex-direction: column;
            &__item{
                max-width: 100%;
            }
        }

    }

    @media screen and( max-width: 480px ){
        h4{
            text-align: center;
        }
    }

</style>
